<template>
  <div class="admin-overview screenHeight">
    <h1>Admin Dashboard</h1>
    <!-- DOMAINS -->
    <div class="card-grid">
       <RouterLink to="/admin/domains" class="dashboard-card action">
        <div class="card-header">
          <i class="fa-solid fa-globe card-icon" aria-hidden="true" />
          <span class="card-label">{{ $t('components.adminDashboard.allDomains') }}</span>
        </div>
        <span class="card-count">{{ domainsCount }}</span>
        <span class="card-foot primary">{{ $t('components.adminDashboard.showAllDomais') }} →</span>
      </RouterLink>


      <!-- USERS -->
      <RouterLink to="/admin/users" class="dashboard-card action">
        <div class="card-header">
          <i class="fa-solid fa-users card-icon" aria-hidden="true" />
          <span class="card-label">{{ $t('components.adminDashboard.allUsers') }}</span>
        </div>
        <span class="card-count">{{ usersCount }}</span>
        <span class="card-foot primary">{{ $t('components.adminDashboard.showAllUsers') }} →</span>
      </RouterLink>

      <!-- SUPPLIER USERS -->
      <RouterLink to="/admin/supplierusers" class="dashboard-card action">
        <div class="card-header">
          <i class="fa-solid fa-industry card-icon" aria-hidden="true" />
          <span class="card-label">{{ $t('components.adminDashboard.allSuppliers') }}</span>
        </div>
        <span class="card-count">{{ supplierUsersCount }}</span>
        <span class="card-foot primary">{{ $t('components.adminDashboard.showAllSuppliers') }} →</span>
      </RouterLink>

      <!-- Placeholder -->
      <div class="dashboard-card">
        <div class="card-header">
          <i class="fa-solid fa-envelope-open-text card-icon" aria-hidden="true" />
          <span class="card-label">{{ $t('components.adminDashboard.pendingInvites') }}</span>
        </div>
        <span class="card-count">{{ pendingInvites }}</span>
        <span class="card-foot text-sm">{{ $t('components.adminDashboard.pendingInvitesNumber') }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import type { User } from '@/types/api/User/User'
import { getAllUsers, getMetricsForAdminDashboard } from '@/services/api/userService'

const domainsCount = ref<number>(0)
const usersCount = ref<number>(0)
const supplierUsersCount = ref<number>(0)
const pendingInvites = ref<number>(3)

async function loadStats() {
  try {
    const metrics = await getMetricsForAdminDashboard();
    domainsCount.value = metrics.domainCount;
    usersCount.value = metrics.userCount;
    supplierUsersCount.value = metrics.supplierCount;
  } catch (err) {
    console.error('Could not load dashboard stats', err)
  }
}

onMounted(() => {
  loadStats()
  const id = window.setInterval(loadStats, 5 * 60 * 1000)
  onUnmounted(() => clearInterval(id))
})
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

.admin-overview {
  padding: 1.5rem;
  background-color: var(--color-page-bg, #f6f6f6);
  overflow-y: auto;
}

.card-grid {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); 
}

@media (min-width: 992px) {
  .card-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.dashboard-card {
  background-color: var(--color-surface, #ffffff);
  border-radius: var(--corner-radius, 8px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  padding: 1.5rem;
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &.action {
    cursor: pointer;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  .card-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
    text-transform: uppercase;
    font-size: 0.75rem;
    color: var(--el-text-color-placeholder);
  }

  .card-icon {
    font-size: 1.25rem;
    color: var(--el-text-color-secondary, #9e9e9e);
  }

  .card-count {
    display: block;
    margin-bottom: 0.25rem; 
    font-size: 2.5rem;
    font-weight: var(--font-weight-bold, 700);
    color: var(--el-color-primary);
    line-height: 1;
  }

  .card-foot {
    font-size: 0.875rem;
    display: inline-block;
  }
}
</style>